import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import i18next from 'i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Create Auth Context
const AuthContext = createContext();

// Custom hook to use Auth Context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Authentication Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Debug logging with timestamp
  const debugAuth = (message, data = '') => {
    const timestamp = new Date().toLocaleTimeString();
    console.log(`[AUTH DEBUG ${timestamp}] ${message}`, data);
  };

  // Initialize authentication on app load (only once)
  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=') || window.location.pathname === '/auth-callback') {
      debugAuth('OAuth callback detected, skipping init - AuthCallback will handle it');
      setLoading(false);
      return;
    }
    if (!initialized) {
      debugAuth('Initializing auth for the first time...');
      initializeAuth();
    }
  }, [initialized]);

  // Initialize authentication from localStorage
  const initializeAuth = async () => {
    if (initialized) {
      debugAuth('Auth already initialized, skipping...');
      return;
    }

    debugAuth('Checking localStorage for token...');
    
    try {
      const token = localStorage.getItem('skillsos_token');
      const storedUser = localStorage.getItem('skillsos_user');
      
      if (token && storedUser) {
        debugAuth('Found stored token and user', { 
          token: token.substring(0, 20) + '...', 
          tokenLength: token.length,
          user: JSON.parse(storedUser).email 
        });
        
        // Parse user data safely
        let userData;
        try {
          userData = JSON.parse(storedUser);
        } catch (parseError) {
          debugAuth('Failed to parse stored user data', parseError);
          clearAuthData();
          return;
        }
        
        // Set axios header
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // Set user from stored data first (for immediate UI update)
        setUser(userData);
        setIsAuthenticated(true);
        
        // Then verify token is still valid (async, but don't block UI)
        setTimeout(async () => {
          try {
            const response = await axios.get(`${API}/auth/me`);
            debugAuth('Background token verification successful', response.data.email);
            
            // Update with fresh user data from server
            setUser(response.data);
          } catch (error) {
            debugAuth('Background token verification failed', {
              status: error.response?.status,
              message: error.message
            });
            
            // Only clear auth data if it's definitely a 401 (unauthorized)
            // Be more lenient with network errors
            if (error.response?.status === 401) {
              debugAuth('401 Unauthorized - clearing auth data');
              clearAuthData('Background token verification failed - 401');
            } else {
              debugAuth('Non-401 error, keeping stored auth data for now');
            }
          }
        }, 100); // Small delay to not block UI
        
      } else {
        debugAuth('No stored authentication data found');
      }
    } catch (error) {
      debugAuth('Auth initialization error', error);
      // Don't clear on initialization errors unless it's serious
      if (error.name === 'SyntaxError') {
        debugAuth('Corrupted localStorage data, clearing...');
        clearAuthData('Corrupted localStorage data');
      }
    } finally {
      setInitialized(true);
      setLoading(false);
    }
  };

  // Login function
  const login = async (email, password) => {
    debugAuth('Login attempt', { email });
    
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password
      });

      debugAuth('Login response received', response.data);

      const { access_token, user: userData } = response.data;

      if (access_token && userData) {
        debugAuth('Storing new authentication data...');
        
        // Store authentication data
        localStorage.setItem('skillsos_token', access_token);
        localStorage.setItem('skillsos_user', JSON.stringify(userData));
        
        // Set axios header
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        
        // Update state
        setUser(userData);
        setIsAuthenticated(true);
        
        // Mark as initialized to prevent re-initialization
        setInitialized(true);
        
        debugAuth('Login successful - token stored', { 
          user: userData.email, 
          tokenLength: access_token.length 
        });
        toast.success(i18next.t('loginSuccessful') || 'Login successful!');
        
        return { success: true, user: userData };
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      debugAuth('Login failed', error.response?.data);
      
      // Handle FastAPI validation errors which come as an array
      let errorMessage;
      const errorDetail = error.response?.data?.detail;
      
      if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail[0]?.msg || i18next.t('loginFailed') || 'Login failed';
      } else if (typeof errorDetail === 'string') {
        // Translate common error messages from backend
        if (errorDetail === 'Invalid credentials') {
          errorMessage = i18next.t('invalidCredentials') || 'Невалидни данни за вход';
        } else if (errorDetail === 'User not found') {
          errorMessage = i18next.t('userNotFound') || 'Потребителят не е намерен';
        } else {
          errorMessage = errorDetail;
        }
      } else if (typeof errorDetail === 'object' && errorDetail?.msg) {
        errorMessage = errorDetail.msg;
      } else {
        errorMessage = i18next.t('loginFailed') || 'Login failed';
      }
      
      toast.error(errorMessage);
      
      return { success: false, error: errorMessage };
    }
  };

  // Register function
  const register = async (userData) => {
    debugAuth('Registration attempt', { email: userData.email });
    
    try {
      const response = await axios.post(`${API}/auth/register`, userData);
      debugAuth('Registration response received', response.data);

      const { access_token, user: newUserData } = response.data;

      if (access_token && newUserData) {
        // Store authentication data
        localStorage.setItem('skillsos_token', access_token);
        localStorage.setItem('skillsos_user', JSON.stringify(newUserData));
        
        // Set axios header
        axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
        
        // Update state
        setUser(newUserData);
        setIsAuthenticated(true);
        
        debugAuth('Registration successful', { user: newUserData.email });
        toast.success(i18next.t('registrationSuccessful') || 'Registration successful!');
        
        // Track registration conversion in Meta Pixel
        if (typeof window !== 'undefined' && window.fbq) {
          window.fbq('track', 'CompleteRegistration', {
            content_name: 'User Registration',
            status: 'success'
          });
          debugAuth('Meta Pixel: CompleteRegistration event fired');
        }
        
        // Track registration conversion in Google Analytics
        if (typeof window !== 'undefined' && window.gtag) {
          window.gtag('event', 'sign_up', {
            method: 'email'
          });
          debugAuth('Google Analytics: sign_up event fired');
        }
        
        return { success: true, user: newUserData };
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      debugAuth('Registration failed', error.response?.data);
      
      // Handle FastAPI validation errors which come as an array
      let errorMessage;
      const errorDetail = error.response?.data?.detail;
      
      if (Array.isArray(errorDetail)) {
        // Extract message from first validation error
        errorMessage = errorDetail[0]?.msg || i18next.t('registrationFailed') || 'Registration failed';
      } else if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (typeof errorDetail === 'object' && errorDetail?.msg) {
        errorMessage = errorDetail.msg;
      } else {
        errorMessage = i18next.t('registrationFailed') || 'Registration failed';
      }
      
      toast.error(errorMessage);
      
      return { success: false, error: errorMessage };
    }
  };

  // Logout function
  const logout = () => {
    debugAuth('Logout initiated by user');
    clearAuthData('User logout');
    toast.success(i18next.t('logoutSuccessful') || 'Logged out successfully');
  };

  // Clear all authentication data
  const clearAuthData = (reason = 'Unknown') => {
    debugAuth('⚠️ CLEARING AUTH DATA', { reason, stack: new Error().stack });
    
    localStorage.removeItem('skillsos_token');
    localStorage.removeItem('skillsos_user');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setIsAuthenticated(false);
    
    debugAuth('Auth data cleared completely');
  };

  // Update user profile
  const updateUser = (updatedUserData) => {
    debugAuth('Updating user profile', updatedUserData);
    
    const updatedUser = { ...user, ...updatedUserData };
    setUser(updatedUser);
    localStorage.setItem('skillsos_user', JSON.stringify(updatedUser));
    
    debugAuth('User profile updated');
  };

  // Check if user is authenticated (no role-based access control)
  const isUserAuthenticated = () => {
    const hasToken = !!localStorage.getItem('skillsos_token');
    const hasUser = !!localStorage.getItem('skillsos_user');
    debugAuth('Auth check', { isAuthenticated, hasUser, hasToken });
    return isAuthenticated && user && hasToken;
  };

  // Check if email already exists in the system
  const checkEmailExists = async (email) => {
    debugAuth('Checking if email exists', { email });
    
    try {
      const response = await axios.post(`${API}/auth/check-email`, { email });
      debugAuth('Email check result', response.data);
      return response.data.exists;
    } catch (error) {
      debugAuth('Email check failed', error);
      // Default to false (assume new user) if check fails
      return false;
    }
  };

  // Helper function to refresh authentication if needed
  const refreshAuth = async () => {
    const token = localStorage.getItem('skillsos_token');
    if (!token) {
      debugAuth('No token for refresh');
      return false;
    }

    try {
      // Set header in case it was lost
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      const response = await axios.get(`${API}/auth/me`);
      debugAuth('Auth refresh successful');
      
      setUser(response.data);
      setIsAuthenticated(true);
      return true;
    } catch (error) {
      debugAuth('Auth refresh failed', error.response?.status);
      if (error.response?.status === 401) {
        clearAuthData('Token expired during refresh');
      }
      return false;
    }
  };

  // Login with token directly (for Google OAuth callback)
  const loginWithToken = (accessToken, userData) => {
    debugAuth('loginWithToken called', { email: userData?.email });
    
    if (accessToken && userData) {
      localStorage.setItem('skillsos_token', accessToken);
      localStorage.setItem('skillsos_user', JSON.stringify(userData));
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
      setUser(userData);
      setIsAuthenticated(true);
      setInitialized(true);
      
      // Track Google sign-up in analytics
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'sign_up', { method: 'google' });
      }
      if (typeof window !== 'undefined' && window.fbq) {
        window.fbq('track', 'CompleteRegistration', {
          content_name: 'Google OAuth Registration',
          status: 'success'
        });
      }
      
      debugAuth('loginWithToken successful', { user: userData.email });
    }
  };

  // Auth context value
  const value = {
    // State
    user,
    loading,
    isAuthenticated,
    
    // Actions
    login,
    loginWithToken,
    register,
    logout,
    updateUser,
    checkEmailExists,
    
    // Utilities
    isUserAuthenticated,
    refreshAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};